/**
 * The account menu, grown from the Compte tab of the phone bottom bar.
 *
 * The web half of `apps/mobile/src/shared/ui/bottom-nav/account-menu.tsx`:
 * tapping the account tab opens a panel above the bar instead of routing away
 * to /account. Same rows, same order, same header with the gear.
 *
 * It differs from the app in one place, on the owner's call (2026-09-13): the
 * web panel keeps Se déconnecter, because the site's avatar menu on desktop
 * has always had it and the two menus should not disagree.
 *
 * Rows do not navigate. Each one opens the account popup on its own panel, so
 * the page underneath survives — the whole point of the change. Bucket is the
 * exception: it moved here from its own bar tab and goes to the bucket page.
 * Guests get the panel too, with just Bucket and Sign in.
 *
 * MaJourneys AI, Transport and Plan your trip left this menu for the +
 * beside the bar (owner, 2026-10-18), so each is reached one way.
 */

import { useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Calendar, LogIn, LogOut, Settings, ShoppingBag, User, type LucideIcon } from "lucide-react";
import { useSession } from "../../lib/auth";
import type { TabId as AccountTabId } from "../../features/account/components/account-modal";
import { floatPanelStyle } from "../ui/use-presence";

const NAVY = "#110E39";
const MUTED = "#5a5d77";
const RED = "#9B2C2C";
const HAIRLINE = "rgba(17,14,57,0.09)";
const SOFT_TINT = "rgba(17,14,57,0.055)";
const NAVY_GRADIENT = "linear-gradient(135deg, #2E2878 0%, #1A1454 48%, #110E39 100%)";

const ROWS: readonly { key: AccountTabId; labelKey: string; Icon: LucideIcon }[] = [
  { key: "details", labelKey: "common.profile", Icon: User },
  { key: "bookings", labelKey: "account_modal.tab_bookings", Icon: Calendar },
  // Saved, Reviews and Rewards left the menu (owner, 2026-10-14); their
  // panels still open from the account popup's own tabs.
];

export function BottomBarAccountMenu({
  state,
  panelRef,
  isAuthed,
  onPick,
  onNavigate,
  onSignOut,
}: {
  readonly state: "open" | "closed";
  readonly panelRef: (el: HTMLElement | null) => void;
  readonly isAuthed: boolean;
  readonly onPick: (tab: AccountTabId) => void;
  readonly onNavigate: (href: string) => void;
  readonly onSignOut: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { lang } = useParams({ strict: false }) as { lang?: string };
  const locale = lang ?? "en";
  const { data: session } = useSession();
  const user = session?.user as { name?: string; email?: string; image?: string } | undefined;

  const displayName = user?.name ?? user?.email ?? t("nav.account");
  const initial = displayName.trim()[0]?.toUpperCase() ?? "?";

  const rowClass =
    "flex w-full items-center gap-3 px-3.5 py-3 text-start text-[14.5px] font-semibold hover:bg-black/5";

  return (
    <div
      ref={panelRef}
      role="menu"
      aria-label={t("nav.account")}
      data-state={state}
      className={`mj-pop absolute inset-x-0 bottom-[calc(100%+8px)] z-[var(--z-dropdown)] overflow-hidden ${
        state === "closed" ? "pointer-events-none" : ""
      }`}
      style={{
        ...floatPanelStyle(22),
        // The panel grows from the corner nearest the tab that opened it.
        transformOrigin: i18n.dir() === "rtl" ? "bottom left" : "bottom right",
      }}
    >
      {isAuthed ? (
        <>
        <div
          className="flex items-center gap-2.5 px-3.5 py-3"
          style={{ borderBottom: `1px solid ${HAIRLINE}` }}
        >
          <span
            className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full"
            style={{ background: NAVY_GRADIENT }}
          >
            {user?.image ? (
              <img src={user.image} alt="" className="h-full w-full object-cover" />
            ) : (
              <span className="text-[12px] font-bold text-white">{initial}</span>
            )}
          </span>
          <span className="min-w-0 flex-1 line-clamp-2 break-words text-[14px] font-semibold" style={{ color: NAVY }}>
            {displayName}
          </span>
          <button
            type="button"
            role="menuitem"
            onClick={() => onPick("preferences")}
            aria-label={t("account_modal.tab_preferences")}
            className="mj-press grid h-9 w-9 shrink-0 place-items-center rounded-full"
          >
            <Settings size={17} strokeWidth={2} style={{ color: MUTED }} aria-hidden />
          </button>
        </div>

        {ROWS.map((row, i) => (
          <button
            key={row.key}
            type="button"
            role="menuitem"
            onClick={() => onPick(row.key)}
            className={rowClass}
            style={{ color: NAVY, background: i === 0 ? SOFT_TINT : undefined }}
          >
            <row.Icon size={17} strokeWidth={2} style={{ color: i === 0 ? NAVY : MUTED }} aria-hidden />
            <span className="min-w-0 flex-1 break-words">{t(row.labelKey)}</span>
          </button>
        ))}
        </>
      ) : null}

      <button
        type="button"
        role="menuitem"
        onClick={() => onNavigate(`/${locale}/bucket`)}
        className={rowClass}
        style={{ color: NAVY, borderTop: isAuthed ? `1px solid ${HAIRLINE}` : undefined }}
      >
        <ShoppingBag size={17} strokeWidth={2} style={{ color: MUTED }} aria-hidden />
        <span className="min-w-0 flex-1 break-words">{t("nav.bucket")}</span>
      </button>

      {isAuthed ? (
        <button
          type="button"
          role="menuitem"
          onClick={onSignOut}
          className={rowClass}
          style={{ color: RED, borderTop: `1px solid ${HAIRLINE}` }}
        >
          <LogOut size={17} strokeWidth={2} style={{ color: RED }} aria-hidden />
          <span className="min-w-0 flex-1 break-words">{t("common.sign_out")}</span>
        </button>
      ) : (
        <button
          type="button"
          role="menuitem"
          onClick={() => onNavigate("/sign-in")}
          className={rowClass}
          style={{ color: NAVY, borderTop: `1px solid ${HAIRLINE}` }}
        >
          <LogIn size={17} strokeWidth={2} style={{ color: MUTED }} aria-hidden />
          <span className="min-w-0 flex-1 break-words">{t("common.sign_in")}</span>
        </button>
      )}
    </div>
  );
}
